import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import ChangePasswordModal from "./ChangePasswordModal";
import ChangeActionPasswordModal from "./ChangeActionPasswordModal";
import {
  Truck,
  Users,
  FileText,
  Receipt,
  Menu,
  X,
  Phone,
  ShieldCheck,
  FileSpreadsheet,
  Printer,
  ChevronDown,
  FolderKanban,
  Calculator,
  LogOut,
  LogIn,
  User,
  Key,
  LayoutDashboard,
  Bell,
  Lock,
  Palette,
  UserCog,
} from "lucide-react";
import { API_BASE_URL } from "../config/api";
import logoImg from "../assets/logo.png";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isOwner, isOffice, isParty, isTruck } = useAuth();
  const { theme, setTheme, THEMES } = useTheme();

  const [notificationCounts, setNotificationCounts] = useState({
    officeOrders: 0,
    partyOrders: 0,
    truckOrders: 0,
    paymentAlerts: 0,
    truckComing: 0,
  });

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileThemeOpen, setMobileThemeOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isChangeActionPasswordOpen, setIsChangeActionPasswordOpen] = useState(false);
  const dropdownRef = useRef(null);
  const userMenuRef = useRef(null);
  const mobileThemeRef = useRef(null);

  // Fetch unconfirmed orders & alerts count
  const fetchAllNotifications = async () => {
    try {
      const promises = [
        fetch(`${API_BASE_URL}/office-orders`).then((r) => (r.ok ? r.json() : [])).catch(() => []),
        fetch(`${API_BASE_URL}/party-orders`).then((r) => (r.ok ? r.json() : [])).catch(() => []),
        fetch(`${API_BASE_URL}/truck-orders`).then((r) => (r.ok ? r.json() : [])).catch(() => []),
      ];

      if (isOwner) {
        promises.push(
          fetch(`${API_BASE_URL}/lr-entries`).then((r) => (r.ok ? r.json() : [])).catch(() => []),
          fetch(`${API_BASE_URL}/parties`).then((r) => (r.ok ? r.json() : [])).catch(() => [])
        );
      }

      const results = await Promise.all(promises);
      const officeData = Array.isArray(results[0]) ? results[0] : [];
      const partyData = Array.isArray(results[1]) ? results[1] : [];
      const truckData = Array.isArray(results[2]) ? results[2] : [];

      let officeCount = 0;
      let partyCount = 0;
      let truckCount = 0;
      let paymentCount = 0;
      let truckComingCount = 0;

      // Office orders unconfirmed count
      officeCount = officeData.filter((ord) => ord.status !== "CONFIRMED").length;

      // Party orders unconfirmed count
      if (isParty) {
        const uName = String(user?.username || "").trim().toLowerCase();
        const pName = String(user?.partyName || "").trim().toLowerCase();
        partyCount = partyData.filter((ord) => {
          if (ord.status === "CONFIRMED") return false;
          const createdBy = String(ord.createdBy || "").trim().toLowerCase();
          const ordParty = String(ord.partyName || "").trim().toLowerCase();
          return (createdBy && createdBy === uName) || (ordParty && ordParty === pName);
        }).length;
      } else {
        partyCount = partyData.filter((ord) => ord.status !== "CONFIRMED").length;
      }

      // Truck orders unconfirmed count
      if (isTruck) {
        const uName = String(user?.username || "").trim().toLowerCase();
        truckCount = truckData.filter((ord) => {
          if (ord.status === "CONFIRMED") return false;
          const createdBy = String(ord.createdBy || "").trim().toLowerCase();
          return createdBy && createdBy === uName;
        }).length;
      } else {
        truckCount = truckData.filter((ord) => ord.status !== "CONFIRMED").length;
      }

      // If Owner, compute Payment Alerts and Truck Coming Alerts
      if (isOwner && results[3] && results[4]) {
        const lrEntries = Array.isArray(results[3]) ? results[3] : [];
        const partiesList = Array.isArray(results[4]) ? results[4] : [];

        // 1. Payment alerts overdue count
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const pMap = {};
        partiesList.forEach((p) => {
          if (p.partyName) {
            pMap[p.partyName.toUpperCase().trim()] = p.paymentDays !== undefined ? Number(p.paymentDays) : 30;
          }
        });

        paymentCount = lrEntries.filter((lr) => {
          const payStatus = (lr.toPayOrPaid || "TBB").trim().toUpperCase().replace("-", " ");
          if (payStatus === "TO PAY" || payStatus === "TOPAY") return false;
          if (lr.partyPaymentStatus === "PAID") return false;

          const pNameKey = (lr.consigneeName || lr.consignorName || lr.debitAmountTo || "").toUpperCase().trim();
          const timelineDays = pMap[pNameKey] !== undefined ? pMap[pNameKey] : 30;

          let billedDate;
          const dateStr = lr.dateTime || lr.date;
          if (dateStr) {
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
              billedDate = new Date(dateStr + "T00:00:00");
            } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
              const parts = dateStr.split("/");
              billedDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`);
            } else {
              billedDate = new Date(dateStr);
            }
          } else if (lr.createdAt) {
            billedDate = new Date(lr.createdAt);
          } else {
            billedDate = new Date();
          }

          if (isNaN(billedDate.getTime())) return false;
          billedDate.setHours(0, 0, 0, 0);

          const dueDate = new Date(billedDate);
          dueDate.setDate(dueDate.getDate() + timelineDays);

          const diffDays = Math.round((today.getTime() - dueDate.getTime()) / (1000 * 3600 * 24));
          return diffDays > 0;
        }).length;

        // 2. Truck Coming alerts count
        const lrsByTruck = {};
        lrEntries.forEach((lr) => {
          const tNo = (lr.truckNo || "").toUpperCase().trim();
          if (!tNo) return;
          if (!lrsByTruck[tNo]) lrsByTruck[tNo] = [];
          lrsByTruck[tNo].push(lr);
        });

        Object.keys(lrsByTruck).forEach((tNo) => {
          const truckLRs = lrsByTruck[tNo];
          const parseD = (dStr, cStr) => {
            if (dStr) {
              if (/^\d{4}-\d{2}-\d{2}$/.test(dStr)) return new Date(dStr + "T00:00:00");
              if (/^\d{2}\/\d{2}\/\d{4}$/.test(dStr)) {
                const parts = dStr.split("/");
                return new Date(`${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`);
              }
              const d = new Date(dStr);
              if (!isNaN(d.getTime())) return d;
            }
            if (cStr) {
              const c = new Date(cStr);
              if (!isNaN(c.getTime())) return c;
            }
            return new Date();
          };

          truckLRs.sort((a, b) => parseD(b.dateTime, b.createdAt).getTime() - parseD(a.dateTime, a.createdAt).getTime());
          const latestLr = truckLRs[0];
          if (latestLr.truckComingDismissed) return;

          const lrDate = parseD(latestLr.dateTime, latestLr.createdAt);
          lrDate.setHours(0, 0, 0, 0);

          const tripDays = latestLr.tripDays !== undefined ? Number(latestLr.tripDays) : 5;
          const diffDays = Math.floor((today.getTime() - lrDate.getTime()) / (1000 * 3600 * 24));
          if (diffDays >= tripDays) {
            truckComingCount++;
          }
        });
      }

      setNotificationCounts({
        officeOrders: officeCount,
        partyOrders: partyCount,
        truckOrders: truckCount,
        paymentAlerts: paymentCount,
        truckComing: truckComingCount,
      });
    } catch (e) {
      console.error("Error fetching notifications count in navbar:", e);
    }
  };

  useEffect(() => {
    if (user) {
      fetchAllNotifications();
      const interval = setInterval(fetchAllNotifications, 15000);
      return () => clearInterval(interval);
    }
  }, [user, isOwner, isOffice, isParty, isTruck, location.pathname]);

  const getItemBadgeCount = (path) => {
    if (path === "/office-orders") return notificationCounts.officeOrders;
    if (path === "/party-orders") return notificationCounts.partyOrders;
    if (path === "/truck-orders") return notificationCounts.truckOrders;
    if (path === "/payment-alerts") return notificationCounts.paymentAlerts;
    if (path === "/truck-coming") return notificationCounts.truckComing;
    return 0;
  };

  const totalDropdownNotifications =
    notificationCounts.officeOrders +
    notificationCounts.partyOrders +
    notificationCounts.truckOrders +
    notificationCounts.paymentAlerts +
    notificationCounts.truckComing;

  // Primary Navigation Items for Owner
  const ownerPrimaryItems = [
    { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
    { name: "L/R Entry", path: "/lr-entry", icon: Truck },
    { name: "LR Records", path: "/lr-list", icon: FileText },
    { name: "Freight Receipt", path: "/freight-receipt", icon: Receipt },
  ];

  // Primary Navigation Items for Office Role
  const officePrimaryItems = [
    { name: "L/R Entry", path: "/lr-entry", icon: Truck },
    { name: "LR Records", path: "/lr-list", icon: FileText },
    { name: "Freight Receipt", path: "/freight-receipt", icon: Receipt },
    { name: "Office Orders", path: "/office-orders", icon: FileText },
  ];

  // Primary Navigation Items for Party
  const partyPrimaryItems = [
    { name: "My Accounting", path: "/accounting", icon: Calculator },
    { name: "My LR Records", path: "/party-lr-records", icon: FileText },
    { name: "Party Orders", path: "/party-orders", icon: FileText },
  ];

  // Primary Navigation Items for Truck Owner
  const truckPrimaryItems = [
    { name: "My Truck Accounting", path: "/truck-accounting", icon: Truck },
    { name: "Truck Orders", path: "/truck-orders", icon: Truck },
  ];

  const primaryItems = isTruck ? truckPrimaryItems : isParty ? partyPrimaryItems : isOffice ? officePrimaryItems : ownerPrimaryItems;

  // Dropdown Items (Owner only)
  const dropdownItems = [
    { name: "Office Orders", path: "/office-orders", icon: FileText },
    { name: "Party Orders", path: "/party-orders", icon: FileText },
    { name: "Truck Orders", path: "/truck-orders", icon: Truck },
    { name: "Daily Report", path: "/daily-report", icon: FileText },
    { name: "Accounting", path: "/accounting", icon: Calculator },
    { name: "Range LR Print", path: "/range-lr-print", icon: Printer },
    { name: "Party Statement", path: "/party-statement", icon: FileSpreadsheet },
    { name: "CA Excel", path: "/ca-excel", icon: FileSpreadsheet },
    { name: "Party Master", path: "/party-master", icon: Users },
    { name: "Truck Master", path: "/truck-master", icon: Truck },
    { name: "Letter Pad", path: "/letter-pad", icon: FileText },
    { name: "Truck Debit", path: "/truck-payments", icon: Truck },
    { name: "Payment Alerts", path: "/payment-alerts", icon: Bell },
    { name: "Truck Coming", path: "/truck-coming", icon: Truck },
    { name: "User Management", path: "/users", icon: UserCog },
  ];

  const isActive = (path) => {
    if (path === "/dashboard" && (location.pathname === "/" || location.pathname === "/dashboard")) return true;
    return location.pathname.startsWith(path);
  };

  const isDropdownActive = dropdownItems.some((item) => isActive(item.path));

  // Close dropdowns when user clicks outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (mobileThemeRef.current && !mobileThemeRef.current.contains(event.target)) {
        setMobileThemeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Hide Navbar before login or on /login page
  if (!user || location.pathname === "/login") {
    return null;
  }

  return (
    <header className="bg-white text-slate-900 shadow-md sticky top-0 z-50 border-b border-slate-200 font-sans">

      {/* Main Navbar */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16 gap-3">

          {/* Left Side: Logo / Brand */}
          <Link to={isParty ? "/accounting" : isTruck ? "/truck-accounting" : isOffice ? "/lr-entry" : "/"} className="flex items-center space-x-2.5 shrink-0 group">
            {/* Crown & W Logo */}
            <div className="h-9 sm:h-11 w-9 sm:w-11 overflow-hidden flex items-start justify-center shrink-0">
              <img
                src={logoImg}
                alt="Wolego Transport Logo"
                className="h-12 sm:h-14 w-auto object-cover object-top transform group-hover:scale-105 transition-transform"
              />
            </div>
            <div className="flex flex-col justify-center items-center text-center">
              <span className="font-extrabold text-sm sm:text-base tracking-tight text-[#009a44] font-serif leading-none whitespace-nowrap">
                WOLEGO TRANSPORT
              </span>
              <span className="text-[9px] sm:text-[10px] font-black italic tracking-widest text-[#0072bc] uppercase leading-tight font-serif whitespace-nowrap mt-0.5 text-center w-full">
                EVERYTHING IS FAST
              </span>
            </div>
          </Link>

          {/* Center: Desktop Navigation Links */}
          <nav className="hidden md:flex items-center space-x-1 sm:space-x-1.5 lg:space-x-2">
            {/* Primary Nav Items */}
            {primaryItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const badgeCount = getItemBadgeCount(item.path);
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-bold transition-all whitespace-nowrap relative ${active
                      ? "bg-amber-500 text-slate-950 shadow-md font-extrabold"
                      : "text-slate-700 hover:bg-slate-100 hover:text-amber-600"
                    }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="whitespace-nowrap">{item.name}</span>
                  {badgeCount > 0 && (
                    <span className="px-1.5 py-0.2 rounded-full bg-rose-600 text-white text-[10px] font-black leading-tight animate-pulse shadow-sm">
                      {badgeCount}
                    </span>
                  )}
                </Link>
              );
            })}

            {/* Dropdown Menu for Owner Only */}
            {isOwner && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  onMouseEnter={() => setDropdownOpen(true)}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-bold transition-all cursor-pointer whitespace-nowrap relative ${isDropdownActive
                      ? "bg-amber-500 text-slate-950 shadow-md font-extrabold"
                      : "text-slate-700 hover:bg-slate-100 hover:text-amber-600"
                    }`}
                >
                  <FolderKanban className="w-4 h-4 shrink-0" />
                  <span className="whitespace-nowrap">Reports & Master</span>
                  {totalDropdownNotifications > 0 && (
                    <span
                      className="px-1.5 py-0.2 rounded-full bg-rose-600 text-white text-[10px] font-black leading-tight animate-pulse shadow-sm"
                      title={`${totalDropdownNotifications} Active Alerts & Pending Orders`}
                    >
                      {totalDropdownNotifications}
                    </span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {/* Dropdown Card */}
                {dropdownOpen && (
                  <div
                    onMouseLeave={() => setDropdownOpen(false)}
                    className="absolute left-0 mt-1 w-60 bg-white border border-slate-200 rounded-xl shadow-2xl py-2 z-50 divide-y divide-slate-100 animate-fadeIn"
                  >
                    {dropdownItems.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);
                      const badgeCount = getItemBadgeCount(item.path);
                      return (
                        <Link
                          key={item.name}
                          to={item.path}
                          onClick={() => setDropdownOpen(false)}
                          className={`flex items-center justify-between px-4 py-2.5 text-sm font-semibold transition-all ${active
                              ? "bg-amber-50 text-amber-800 font-extrabold border-l-4 border-amber-500"
                              : "text-slate-700 hover:bg-slate-100 hover:text-amber-600"
                            }`}
                        >
                          <div className="flex items-center space-x-3">
                            <Icon className="w-4 h-4 text-amber-600" />
                            <span>{item.name}</span>
                          </div>
                          {badgeCount > 0 && (
                            <span className="px-2 py-0.5 rounded-full bg-rose-600 text-white text-xs font-black shadow animate-pulse">
                              {badgeCount}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Right Side: Auth / User Status Dropdown Menu */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="bg-slate-100 hover:bg-slate-200 border border-slate-300 px-3 py-1.5 rounded-xl flex items-center gap-2 transition cursor-pointer group shadow-sm"
                >
                  <div className="p-1 bg-amber-100 rounded-lg group-hover:bg-amber-200 transition">
                    <User className="w-4 h-4 text-amber-700" />
                  </div>
                    <div className="text-xs text-left pr-1">
                      <div className="font-extrabold text-amber-800 leading-none flex items-center gap-1">
                        <span>{isOwner ? "OWNER" : isOffice ? "OFFICE" : isTruck ? "TRUCK OWNER" : user.partyName || user.username}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono font-medium leading-tight mt-0.5">
                        {user.username}
                      </div>
                    </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""}`} />
                </button>

                {/* User Dropdown Card */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 animate-fadeIn overflow-hidden">
                    {/* Header info */}
                    <div className="px-4 py-3 bg-gradient-to-r from-amber-500/10 via-amber-400/5 to-transparent border-b border-slate-100">
                      <div className="text-xs font-black text-slate-800 uppercase tracking-wider">Logged In As</div>
                      <div className="text-sm font-extrabold text-amber-800 mt-0.5">
                        {isOwner ? "👑 OWNER ADMIN" : isOffice ? "🏢 OFFICE USER" : isTruck ? "🚛 TRUCK OWNER" : `🏢 ${user.partyName || user.username}`}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono">User: {user.username}</div>
                    </div>

                    <div className="p-2 space-y-1">
                      {/* Theme Picker inside Dropdown */}
                      <div className="p-2 bg-slate-50 border border-slate-200/60 rounded-xl">
                        <div className="text-[11px] font-extrabold text-purple-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                          <Palette className="w-3.5 h-3.5 text-purple-600" />
                          <span>Color Theme (थीम)</span>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          {THEMES.map((t) => (
                            <button
                              key={t.id}
                              type="button"
                              onClick={() => setTheme(t.id)}
                              className={`text-left px-2 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                                theme === t.id
                                  ? "bg-amber-500 text-slate-950 font-extrabold shadow"
                                  : "bg-white hover:bg-slate-200 text-slate-700 border border-slate-200"
                              }`}
                            >
                              <span className="text-sm">{t.icon}</span>
                              <span className="truncate">{t.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Owner Security Password */}
                      {isOwner && (
                        <button
                          type="button"
                          onClick={() => {
                            setUserMenuOpen(false);
                            setIsChangeActionPasswordOpen(true);
                          }}
                          className="w-full text-left px-3 py-2.5 rounded-xl text-xs font-bold text-slate-700 hover:bg-emerald-50 hover:text-emerald-800 flex items-center gap-2.5 transition cursor-pointer group"
                        >
                          <div className="p-1.5 bg-emerald-100 text-emerald-700 rounded-lg group-hover:bg-emerald-200 transition">
                            <ShieldCheck className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="font-extrabold text-slate-800 group-hover:text-emerald-800">Security Password</div>
                            <div className="text-[10px] text-slate-500 font-normal">Action & Debit Passcode</div>
                          </div>
                        </button>
                      )}

                      {/* Change Login Password */}
                      <button
                        type="button"
                        onClick={() => {
                          setUserMenuOpen(false);
                          setIsChangePasswordOpen(true);
                        }}
                        className="w-full text-left px-3 py-2.5 rounded-xl text-xs font-bold text-slate-700 hover:bg-amber-50 hover:text-amber-800 flex items-center gap-2.5 transition cursor-pointer group"
                      >
                        <div className="p-1.5 bg-amber-100 text-amber-700 rounded-lg group-hover:bg-amber-200 transition">
                          <Key className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-extrabold text-slate-800 group-hover:text-amber-800">Change Password</div>
                          <div className="text-[10px] text-slate-500 font-normal">Login Password</div>
                        </div>
                      </button>

                      <div className="pt-1 border-t border-slate-100">
                        {/* Logout */}
                        <button
                          type="button"
                          onClick={() => {
                            setUserMenuOpen(false);
                            handleLogout();
                          }}
                          className="w-full text-left px-3 py-2.5 rounded-xl text-xs font-bold text-rose-600 hover:bg-rose-50 flex items-center gap-2.5 transition cursor-pointer group"
                        >
                          <div className="p-1.5 bg-rose-100 text-rose-600 rounded-lg group-hover:bg-rose-200 transition">
                            <LogOut className="w-4 h-4" />
                          </div>
                          <span className="font-extrabold">Logout</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 px-3 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-xl transition shadow"
              >
                <LogIn className="w-4 h-4" />
                <span>Login</span>
              </Link>
            )}
          </div>

          {/* Mobile header controls */}
          <div className="md:hidden flex items-center gap-1.5 shrink-0 pr-1 sm:pr-0">
            {user && (
              <>
                {/* Compact Theme Popover Button for Mobile Header */}
                <div className="relative" ref={mobileThemeRef}>
                  <button
                    type="button"
                    onClick={() => setMobileThemeOpen(!mobileThemeOpen)}
                    className="p-1.5 text-purple-700 hover:bg-purple-100 rounded-xl bg-purple-50 border border-purple-200 cursor-pointer"
                    title="Choose Theme"
                  >
                    <Palette className="w-4 h-4" />
                  </button>
                  {mobileThemeOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-2xl py-1 z-50 animate-fadeIn">
                      {THEMES.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            setTheme(t.id);
                            setMobileThemeOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs font-bold flex items-center gap-2 transition-all ${
                            theme === t.id
                              ? "bg-amber-50 text-amber-900 font-extrabold border-l-4 border-amber-500"
                              : "text-slate-700 hover:bg-slate-100"
                          }`}
                        >
                          <span>{t.icon}</span>
                          <span>{t.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              title="Toggle Menu"
              className="p-2 rounded-xl text-slate-800 bg-slate-100 hover:bg-slate-200 border border-slate-300 focus:outline-none transition shrink-0 ml-0.5 relative"
            >
              {mobileMenuOpen ? <X className="w-5 h-5 text-rose-600" /> : <Menu className="w-5 h-5 text-slate-800" />}
              {!mobileMenuOpen && totalDropdownNotifications > 0 && (
                <span className="absolute -top-1 -right-1 px-1 min-w-[15px] h-3.5 rounded-full bg-rose-600 text-white text-[9px] font-black flex items-center justify-center animate-pulse shadow">
                  {totalDropdownNotifications}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 px-2 pt-2 pb-6 space-y-1 sm:px-3 shadow-2xl max-h-[calc(100vh-4rem)] overflow-y-auto">

          {/* Mobile Theme Selector Bar */}
          <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg mb-2 flex justify-between items-center">
            <span className="text-xs font-black text-amber-900 flex items-center gap-1 uppercase">
              🎨 Color Theme:
            </span>
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              className="bg-white text-slate-900 font-extrabold text-xs px-2 py-1 rounded border border-amber-300 focus:outline-none cursor-pointer"
            >
              {THEMES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.icon} {t.name}
                </option>
              ))}
            </select>
          </div>

          {user && (
            <div className="px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg mb-2 flex justify-between items-center">
              <div>
                <div className="text-xs font-bold text-amber-700">
                  {isOwner ? "👑 OWNER ADMIN" : isOffice ? "🏢 OFFICE USER" : isTruck ? "🚛 TRUCK OWNER" : `🏢 ${user.partyName || user.username}`}
                </div>
                <div className="text-[11px] text-slate-500 font-mono">Mobile: {user.username}</div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {isOwner && (
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setIsChangeActionPasswordOpen(true);
                    }}
                    className="text-xs text-emerald-950 font-black bg-emerald-300 hover:bg-emerald-400 px-2 py-1 rounded flex items-center gap-1 border border-emerald-400"
                  >
                    <ShieldCheck size={13} /> Action PIN
                  </button>
                )}
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setIsChangePasswordOpen(true);
                  }}
                  className="text-xs text-amber-800 font-bold bg-amber-100 hover:bg-amber-200 px-2 py-1 rounded flex items-center gap-1 border border-amber-300"
                >
                  <Key size={13} /> Login Pass
                </button>
                <button
                  onClick={handleLogout}
                  className="text-xs text-rose-700 font-bold bg-rose-100 hover:bg-rose-200 px-2 py-1 rounded border border-rose-200"
                >
                  Logout
                </button>
              </div>
            </div>
          )}

          {primaryItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const badgeCount = getItemBadgeCount(item.path);
            return (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center justify-between px-3 py-3 rounded-md text-base font-bold ${active
                    ? "bg-amber-500 text-slate-950 font-extrabold"
                    : "text-slate-700 hover:bg-slate-100 hover:text-amber-600"
                  }`}
              >
                <div className="flex items-center space-x-3">
                  <Icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </div>
                {badgeCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-rose-600 text-white text-xs font-black shadow animate-pulse">
                    {badgeCount}
                  </span>
                )}
              </Link>
            );
          })}

          {isOwner && (
            <div className="pt-2 border-t border-slate-200">
              <div className="px-3 text-xs font-bold uppercase text-amber-700 tracking-wider mb-1">
                Reports & Party Master
              </div>
              {dropdownItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const badgeCount = getItemBadgeCount(item.path);
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-semibold ${active
                        ? "bg-amber-50 text-amber-800 font-extrabold border-l-4 border-amber-500"
                        : "text-slate-700 hover:bg-slate-100 hover:text-amber-600"
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="w-4 h-4 text-amber-600" />
                      <span>{item.name}</span>
                    </div>
                    {badgeCount > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-rose-600 text-white text-xs font-black shadow animate-pulse">
                        {badgeCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
        user={user}
      />

      {/* Action Security Password Modal (LR Edit/Delete & Truck Debit) */}
      <ChangeActionPasswordModal
        isOpen={isChangeActionPasswordOpen}
        onClose={() => setIsChangeActionPasswordOpen(false)}
        user={user}
      />
    </header>
  );
}

